import { useEffect, useState, memo } from "react";
import { Modal, Form, InputGroup, Dropdown } from "react-bootstrap";

import BubbleComponent from "./BubbleComponent";
import SearchDropdown from "./SearchDropdown";

import clickElementHandler from "../utils/clickElementHandler";
import formatUsername from "../utils/formatUsername";

import Channel from "../typings/Channel";
import DashboardUser from "../typings/DashboardUser";
import SetState from "../typings/SetState";
import useCheckText from "../hooks/use-checkText";
import ButtonComponent from "./ButtonComponent";

type EditChannelModalProps = {
  show: boolean;
  onClose: SetState<boolean>;
  onEditChannelHandler: (
    name: string,
    description: string,
    owners: Channel["owners"]
  ) => void;
  channel: Channel;
};

const EditChannelModal = ({
  show,
  onClose,
  onEditChannelHandler,
  channel: { owners, name, description },
}: EditChannelModalProps) => {
  const [channelOwners, setChannelOwners] = useState<Channel["owners"]>([]);
  const [channelName, setChannelName] = useState("");
  const [channelDesc, setChannelDesc] = useState("");
  const [users, setUsers] = useState<DashboardUser[]>([]);

  const { error, handleTextChange } = useCheckText(setChannelName);

  useEffect(() => {
    setChannelOwners(owners ? [...owners] : []);
    setChannelName(name ? name.replace("#", "") : "");
    setChannelDesc(description ? description : "");
  }, [owners, name, description]);

  const onSubmitHandler = (e: React.SyntheticEvent) => {
    e.preventDefault();
    onEditChannelHandler(channelName, channelDesc, channelOwners);
    setChannelDesc("");
    setChannelName("");
  };

  let coOwnersBubbles = channelOwners.map((user) => (
    <BubbleComponent
      key={user.username}
      onClick={() => clickElementHandler(user, channelOwners, setChannelOwners)}
    >
      {formatUsername(user.username)}
    </BubbleComponent>
  ));

  let userDropdownItems = users.map((user) => {
    //? userId is used because the object in coOwners (channel.owner) are different from the array of users
    //? and the userId is a suitable field in common
    return !channelOwners.find((i) => i.userId === user.userId) ? (
      <Dropdown.Item
        data-testid={`dropdown-item-${user.username}`}
        key={user.userId}
        onClick={() => {
          clickElementHandler(user, channelOwners, setChannelOwners);
        }}
      >
        {formatUsername(user.username)}
      </Dropdown.Item>
    ) : null;
  });

  return (
    <Modal show={show} onHide={() => onClose(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit Channel</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group>
            <p className="font-weight-bold">
              Unmodified values will remain the same.
            </p>
            <InputGroup>
              <InputGroup.Text>#</InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="channel's name..."
                onChange={(e) => handleTextChange(e.target.value)}
                value={channelName}
              />
            </InputGroup>
            <p className="font-weight-light">
              The hashtag is added automatically. Only letters and numbers are
              allowed.{" "}
            </p>
            <Form.Control
              type="text"
              placeholder="channels's description..."
              onChange={(e) => setChannelDesc(e.target.value)}
              value={channelDesc}
            />
            <Modal.Footer>
              {coOwnersBubbles}
              <SearchDropdown
                title="Add co-owners"
                dropdownItems={userDropdownItems}
                setData={setUsers}
                fetchURL="/api/dashboardUser/findByName/"
              />
            </Modal.Footer>
          </Form.Group>

          <Modal.Footer>
            <ButtonComponent onClick={() => onClose(false)}>
              Close
            </ButtonComponent>
            <ButtonComponent
              type="submit"
              onClick={(e) => onSubmitHandler(e)}
              disabled={error}
            >
              Apply changes
            </ButtonComponent>
          </Modal.Footer>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default memo(EditChannelModal);
